import { useEffect } from "react";
import { LogOut } from "lucide-react";
import { track } from "./lib/analytics";
import { btnPrimary } from "./admin-ui";
import { Doodle, Sticker, WindowBar, titleClass } from "./design-system";
import { Photo } from "./photo";

type GateCourse = {
  slug: string;
  title: string;
  subtitle?: string;
  description: string;
  thumbnailUrl?: string | null;
  basePriceMinor?: number;
  baseCurrency?: string;
  modules: Array<{ lessons: unknown[] }>;
};

const copy = {
  kicker: "Tu espacio en hey404",
  title: "Empieza tu primer curso",
  body: "Todavía no tienes un curso. Cómpralo y aquí mismo vas a ver tus clases, tu progreso y tu certificado.",
  lessons: (modules: number, lessons: number) =>
    `${modules} módulos · ${lessons} clases · Acceso completo`,
  buy: "Comprar el curso",
  otherEmail:
    "¿Ya lo compraste con otro correo? Cierra sesión y entra con ese correo.",
  help: "¿Necesitas ayuda?",
  helpLink: "Escríbenos",
  signOut: "Cerrar sesión",
};

const formatPrice = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat("es", {
    style: "currency",
    currency,
    minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);

/**
 * What a signed-in student without any course sees instead of the platform:
 * the course on sale and a single way forward, buying it. The checkout knows
 * them already, so they pay as themselves.
 */
export function BuyCourseGate({
  course,
  onSignOut,
}: {
  course: GateCourse | undefined;
  onSignOut: () => void;
}) {
  useEffect(() => {
    track("buy_gate_viewed", { course: course?.slug });
  }, [course?.slug]);
  const lessonCount =
    course?.modules.reduce((sum, module) => sum + module.lessons.length, 0) ??
    0;
  const price =
    course?.basePriceMinor && course.baseCurrency
      ? formatPrice(course.basePriceMinor, course.baseCurrency)
      : null;
  return (
    <div className="bg-paper text-ink platform-grid min-h-dvh">
      <header className="border-ink bg-paper border-b-(length:--border-surface) sticky top-0 z-30 flex h-16 items-center px-4 sm:px-5">
        <img src="/logo-final.svg" alt="hey404" className="h-10 w-auto" />
        <button
          type="button"
          onClick={onSignOut}
          className="border-ink bg-bone shadow-brutal border-chip ml-auto cursor-pointer p-2"
          aria-label={copy.signOut}
          title={copy.signOut}
        >
          <LogOut size={17} />
        </button>
      </header>
      <main className="mx-auto grid min-h-[calc(100dvh-4rem)] max-w-5xl place-items-center px-5 py-12 sm:px-8">
        <div className="w-full">
          <Sticker tone="lemon" tilt={-2}>
            {copy.kicker}
          </Sticker>
          <h1 className={`${titleClass.page} mt-5`}>{copy.title}</h1>
          <p className="text-ink-soft mt-3 max-w-2xl text-lg">{copy.body}</p>
          {course ? (
            <div className="relative mt-10">
              {/* Outside the card: its overflow would clip the doodle. */}
              <Doodle
                name="flower-yellow"
                float
                tilt={10}
                className="absolute -right-6 -top-9 z-10 w-14"
              />
              <article className="border-ink bg-bone border-surface shadow-brutal-lg grid overflow-hidden md:grid-cols-[1.1fr_1fr]">
                <div className="border-ink md:border-r-(length:--border-surface) border-b-(length:--border-surface) md:border-b-0">
                  <WindowBar tone="grape" title="hey404" />
                  {course.thumbnailUrl && (
                    <Photo
                      src={course.thumbnailUrl}
                      alt=""
                      className="aspect-[3/2] h-auto w-full object-cover"
                    />
                  )}
                </div>
                <div className="flex flex-col p-6 sm:p-8">
                  <h2 className={titleClass.section}>{course.title}</h2>
                  <p className="text-ink-soft mt-3">
                    {course.subtitle || course.description}
                  </p>
                  {lessonCount > 0 && (
                    <p className="mt-4 font-mono text-xs font-bold uppercase tracking-[0.06em]">
                      {copy.lessons(course.modules.length, lessonCount)}
                    </p>
                  )}
                  <div className="mt-auto pt-8">
                    {price && (
                      <p className="font-display text-4xl font-extrabold">
                        {price}
                      </p>
                    )}
                    <a
                      href={`/checkout?courses=${encodeURIComponent(course.slug)}`}
                      className={`${btnPrimary} mt-4 w-full justify-between px-6 py-4 text-lg`}
                    >
                      {copy.buy} <span aria-hidden="true">↗</span>
                    </a>
                  </div>
                </div>
              </article>
            </div>
          ) : null}
          <p className="text-ink-soft mt-8 text-sm">{copy.otherEmail}</p>
          <p className="text-ink-soft mt-2 text-sm">
            {copy.help}{" "}
            <a
              href="mailto:hola@hey404.com"
              className="text-ink font-bold underline"
            >
              {copy.helpLink}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}
